import { FocusMonitor} from '@angular/cdk/a11y';
import {
  ChangeDetectorRef, Component,
  ContentChild, DoCheck,
  ElementRef, Input,
  OnDestroy, Optional,
  Self, ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import { ErrorStateMatcher} from '@angular/material/core';
import { MatInput} from '@angular/material/input';
import { Subject} from 'rxjs';
import { MessageService} from 'src/app/shared/services/message-service.service';
import { CustomHintDirective} from '../../directives/custom-hint.directive';
import { QuillInputComponent} from '../quill/quill.component';
import { RichTextExtra} from './rich-text.model';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl): boolean {
    return control && control.touched && control.invalid;
  }
}

@Component({
  selector: 'app-rich-text',
  templateUrl: './rich-text.component.html',
  styleUrls: ['./rich-text.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class RichTextComponent implements OnDestroy, DoCheck, ControlValueAccessor {
  @ViewChild(MatInput) textarea: ElementRef<HTMLTextAreaElement>;
  @Input() public placeholder: string;
  @Input() public showLabel?: boolean;
  @Input() public testId = 'rich_text';
  @Input() public inputLabel?: string;
  @Input() public inputHint?: string;
  @Input() public inputHintIcon?: boolean;
  @Input() public inputClass?: string;
  @Input() public warningMessage?: string;
  @Input() public isWarningVisible?: string;
  @Input() public autoFocus?: boolean;
  @ViewChild('matEditor', {
    static: true
  }) matEditor: QuillInputComponent;
  @ContentChild(CustomHintDirective, {
    static: false,
  }) customHint: CustomHintDirective;

  @Input()
  get isRequired(): boolean {
    return this._required;
  }
  set isRequired(value: boolean) {
    this._required = value;
  }
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: boolean) {
    this._readonly = value;
  }
  get maxLength(): number {
    return this._maxLength || null;
  }
  @Input()
  set maxLength(value: number) {
    this._maxLength = value;
  }
  private _extras: Array<RichTextExtra> = [];
  get extras(): Array<RichTextExtra> {
    return this._extras;
  }
  @Input()
  set extras(extras: Array<RichTextExtra>) {
    this._extras = extras;
  }

  @Input() minHeight = 150;

  // tslint:disable-next-line:variable-name
  private _required: boolean;
  private _readonly: boolean;
  private _maxLength: number;
  public focused = false;
  private oldDisabled = null;
  private oldTouched = null;
  private oldValid = null;
  private ngUnsubscribe = new Subject<void>();

  onChange: any = () => {};
  onTouched: any = () => {};
  constructor(
    private messageService: MessageService,
    private cd: ChangeDetectorRef,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Self() @Optional() public ctrl: NgControl
  ) {
    if (this.ctrl) {
      this.ctrl.valueAccessor = this;
    }
  }

  ngDoCheck() {
    if (this.ctrl.disabled !== this.oldDisabled) {
      this.oldDisabled = this.ctrl.disabled;
      this.matEditor?.updateErrorState?.();
      this.cd.markForCheck();
    }
    if (this.ctrl.touched !== this.oldTouched) {
      this.oldTouched = this.ctrl.touched;
      this.matEditor?.updateErrorState?.();
      this.cd.markForCheck();
    }
    if (this.ctrl.valid !== this.oldValid) {
      this.oldValid = this.ctrl.valid;
      this.matEditor?.updateErrorState?.();
      this.cd.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  writeValue(value: any): void { }
  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  errorMatcher() {
    return new CustomFieldErrorMatcher();
  }

  public getErrorMessage(): string {
    if (this.ctrl?.invalid) {
      return this.messageService.getControlErrorMessage(this.ctrl.errors);
    }
    return null;
  }
}
